import type {
    Cart,
    EccoAddDiscountToCartMutationVariables,
    EccoGetMyActiveCartQueryVariables,
    EccoGetMyActiveDataQueryVariables,
    EccoRemoveDiscountFromCartMutationVariables,
    GetMasterProductsQueryVariables,
    ProductQueryResult,
    RecalculateCartMutationVariables,
} from "@graphql/generated/types";
import {
    EccoGetMyActiveCartDocument,
    EccoGetMyActiveDataDocument,
    GetMasterProductsDocument,
} from "@graphql/generated/sdk";
import { updateQuery, updateMutation } from "../helpers";
import { useStore } from "../store";
import { useCallback } from "react";
import { FetchServiceAxios } from "@lib/helpers/fetchServiceAxios";
import type { CreateCartParams } from "@app/api/cart/create-cart/route";
import { useCommerceClient } from "@graphql/useCommerceClient";
import { useLocale } from "next-intl";

export const useCreateMyCart = () => {
    const setCartState = useStore((state) => state.setCart);
    return useCallback(
        async (body: CreateCartParams) => {
            const cart = await FetchServiceAxios.post<CreateCartParams, Cart>(
                `/api/cart/create-cart`,
                body
            );
            setCartState(cart);
            return cart;
        },
        [setCartState]
    );
};

export const useGetMyActiveCart = () => {
    const { baseClient } = useCommerceClient();
    const locale = useLocale();
    const setData = useStore((state) => state.setCart);

    const selector = (data: any) => {
        return data.me.activeCart;
    };

    return updateQuery<EccoGetMyActiveCartQueryVariables>(
        baseClient,
        setData,
        selector,
        EccoGetMyActiveCartDocument,
        {
            locale,
        }
    );
};

export const useGetMyActiveData = () => {
    const { baseClient } = useCommerceClient();
    const locale = useLocale();
    const setData = useStore((state) => state.setActiveData);

    const selector = (data: any) => {
        const payload = {
            cart: undefined,
            wishlist: undefined,
        };
        if (data.me?.activeCart) {
            payload.cart = data.me.activeCart;
        }
        if (data.me?.shoppingLists?.results?.length > 0) {
            payload.wishlist = data.me.shoppingLists.results[0];
        }

        return payload;
    };

    return updateQuery<EccoGetMyActiveDataQueryVariables>(
        baseClient,
        setData,
        selector,
        EccoGetMyActiveDataDocument,
        {
            locale,
        }
    );
};

export const useGetMasterDataForLineItems = () => {
    const { baseClient } = useCommerceClient();
    const setData = useStore((state) => state.setMasterProducts);

    const selector = (data: { products: ProductQueryResult }) => {
        const map = new Map();

        data.products?.results?.forEach((item) => {
            if (!map.has(item.id)) map.set(item.id, item.masterData.current);
        });

        return map;
    };

    const filter = {
        skus: "$skus",
        channelIds: "$channelIds",
    };

    return updateQuery<GetMasterProductsQueryVariables>(
        baseClient,
        setData,
        selector,
        GetMasterProductsDocument,
        filter
    );
};

export const useApplyDiscountCodeToCart = () => {
    const setData = useStore((state) => state.setCart);
    const { client } = useCommerceClient();
    const executeWithVariables = client.eccoAddDiscountToCart;
    return updateMutation<EccoAddDiscountToCartMutationVariables>(executeWithVariables, setData);
};

export const useRemoveDiscountCodeFromCart = () => {
    const setData = useStore((state) => state.setCart);
    const { client } = useCommerceClient();
    const executeWithVariables = client.eccoRemoveDiscountFromCart;
    return updateMutation<EccoRemoveDiscountFromCartMutationVariables>(
        executeWithVariables,
        setData
    );
};

export const useRecalculateCart = () => {
    const setData = useStore((state) => state.setCart);
    const { client } = useCommerceClient();
    const executeWithVariables = client.recalculateCart;
    return updateMutation<RecalculateCartMutationVariables>(executeWithVariables, setData);
};
